import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Avatar from '@material-ui/core/Avatar';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import Collapse from '@material-ui/core/Collapse';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { getPropertType, getPropertyThumbnail } from 'aurora-utils/utils';

import messages from 'aurora-translations';
import { Pill, ADGMImage } from 'aurora-components';
import styles from './styles';

const PropertyCollapse = props => {
  const {
    classes,
    isLease,
    switchType,
    expanded,
    selectable,
    multi,
    data,
    selected,
    setSelected,
    getPropertyTitle,
  } = props;

  const onItemClick = useCallback(
    item => {
      if (multi) {
        const newSelected = [...(selected || [])];
        const alreadyExist = newSelected.some(sel => sel.plotID === item.plotID);
        if (alreadyExist) {
          setSelected(newSelected.filter(sel => sel.plotID !== item.plotID));
        } else {
          setSelected([...newSelected, item]);
        }
        return;
      }

      if (selected?.plotID === item.plotID) {
        setSelected(null);
        return;
      }
      setSelected(item);
    },
    [selected],
  );

  return (
    <Collapse in={expanded}>
      <List className={classNames(classes.list, classes.collapsedList)}>
        {(data?.properties || [])?.map(item => (
          <ListItem
            key={item?.plotID}
            className={classNames(classes.listItem, classes.collapsedListItem)}
            button
            {...(selectable
              ? { onClick: () => onItemClick(item) }
              : {
                  component: RouterLink,
                  to: { pathname: `/app/properties/${item.plotID}`, state: { type: switchType } },
                })}
          >
            {selectable &&
              (multi ? (
                <ListItemIcon className={classes.collapseListItemIcon}>
                  <Checkbox
                    edge='start'
                    checked={selected?.some(build => build.plotID === item.plotID) || false}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': item?.plotID }}
                    classes={{ root: classes.itemCheckbox, checked: classes.itemCheckboxChecked }}
                  />
                </ListItemIcon>
              ) : (
                <ListItemIcon className={classes.collapseListItemIcon}>
                  <Radio
                    edge='start'
                    checked={selected?.plotID === item.plotID || false}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': item?.plotID }}
                    classes={{ root: classes.itemCheckbox, checked: classes.itemCheckboxChecked }}
                  />
                </ListItemIcon>
              ))}
            <ListItemText
              id={item?.plotID}
              primary={
                <div className={classNames(classes.listItemText, classes.collapsedListItemText)}>
                  <div className={classNames(classes.item, classes.avatarItem, classes.collapsedAvatarItem)}>
                    <Avatar
                      className={classes.avatar}
                      variant='rounded'
                      src={getPropertyThumbnail(getPropertType(item?.propertyType), isLease ? 'lease' : 'ownership')}
                      alt='property-thumbnail'
                    />
                    <div>
                      <Typography className={classes.title}>{getPropertyTitle(item).title}</Typography>
                    </div>
                  </div>
                  {/* Mobile view */}
                  <Hidden mdUp>
                    <div className={classes.item}>
                      <ADGMImage name='AspectRatioTwoToneIcon.svg' alt='AspectRatioTwoToneIcon' />
                      <Typography>
                        <FormattedMessage {...messages.area_unit} values={{ s: item?.unitArea || 0 }} />
                      </Typography>
                    </div>
                    <div className={classes.item}>
                      <ADGMImage name='KingBedIcon.svg' alt='KingBedIcon' />
                      <Typography>
                        <FormattedMessage {...messages.num_BR} values={{ num: item?.bedrooms || 0 }} />
                      </Typography>
                    </div>
                    {!!item?.unitIsLeased && (
                      <div className={classNames(classes.item, classes.collapsedBadge)}>
                        <Pill text={<FormattedMessage {...messages.Leased} />} type='warning' />
                      </div>
                    )}
                  </Hidden>
                  {/* Web view */}
                  <Hidden smDown>
                    <div className={classes.item}>
                      <Typography className={classes.subTitle}>
                        <FormattedMessage {...messages.unit_number} />
                      </Typography>
                      <Typography>{item?.unitNumber}</Typography>
                    </div>
                    <div className={classes.item}>
                      <Typography className={classes.subTitle}>
                        <FormattedMessage {...messages.bedroom} />
                      </Typography>
                      <Typography>{item?.bedrooms}</Typography>
                    </div>
                    <div className={classes.item}>
                      <Typography className={classes.subTitle}>
                        <FormattedMessage {...messages.area_size} />
                      </Typography>
                      <Typography>
                        <FormattedMessage {...messages.area_unit} values={{ s: item?.unitArea }} />
                      </Typography>
                    </div>
                    {!!item?.unitIsLeased && (
                      <div className={classes.item}>
                        <Pill text={<FormattedMessage {...messages.Leased} />} type='warning' />
                      </div>
                    )}
                  </Hidden>
                </div>
              }
            />
          </ListItem>
        ))}
      </List>
    </Collapse>
  );
};

PropertyCollapse.propTypes = {
  classes: PropTypes.object.isRequired,
  switchType: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
  selected: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  isLease: PropTypes.bool.isRequired,
  selectable: PropTypes.bool.isRequired,
  multi: PropTypes.bool.isRequired,
  getPropertyTitle: PropTypes.func.isRequired,
  setSelected: PropTypes.func.isRequired,
};

export default withStyles(styles)(PropertyCollapse);
